import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { useNavigate } from 'react-router-dom';
import {
  ServiceArea,
  caregiverServiceAreaService,
} from '../services/caregiverServiceAreaService';

// FEAT-064 — Caregiver Service Areas page (web).
// MVP-18.33 — caregivers select the zip-code service areas they serve,
// gated behind an explicit confirmation agreement.
// Modelled on CaregiverTrustSafetyStep (agreement gate) and the section-card
// + route pattern already established by FEAT-061.

const AGREEMENT_VERSION = '1.0';
const AGREEMENT_LABEL =
  'I confirm I will travel to and serve the areas I select.';

const CaregiverServiceAreasPage: React.FC = () => {
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [available, setAvailable] = useState<ServiceArea[]>([]);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [currentAgreementVersion, setCurrentAgreementVersion] =
    useState<string>(AGREEMENT_VERSION);

  const [loading, setLoading] = useState(true);
  const [submittingAgreement, setSubmittingAgreement] = useState(false);
  const [savingAreas, setSavingAreas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await caregiverServiceAreaService.getServiceAreas();
      setAvailable(result.available);
      setSelected(new Set(result.selected.map((a) => a.id)));
      setAgreementAccepted(result.agreement.accepted);
      setCurrentAgreementVersion(result.currentAgreementVersion);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We had trouble loading your service areas. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAgreementAccept = useCallback(async () => {
    if (!agreementChecked) return;
    setSubmittingAgreement(true);
    setError(null);
    try {
      await caregiverServiceAreaService.acceptAgreement(currentAgreementVersion);
      setAgreementAccepted(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to record your agreement.',
      );
    } finally {
      setSubmittingAgreement(false);
    }
  }, [agreementChecked, currentAgreementVersion]);

  const toggleArea = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setSaveSuccess(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!agreementAccepted) {
      setError('Please accept the service area agreement before saving.');
      return;
    }
    setSavingAreas(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await caregiverServiceAreaService.updateServiceAreas(Array.from(selected));
      setSaveSuccess(true);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to save service areas.';
      if (msg.includes('AGREEMENT_REQUIRED')) {
        setError(
          'Please accept the service area agreement before saving your selections.',
        );
        setAgreementAccepted(false);
      } else {
        setError(msg);
      }
    } finally {
      setSavingAreas(false);
    }
  }, [agreementAccepted, selected]);

  const areaLabel = useCallback((area: ServiceArea) => {
    const parts = [area.zip];
    if (area.city) parts.push(area.city);
    if (area.state) parts.push(area.state);
    return parts.join(', ');
  }, []);

  const selectedCount = selected.size;
  const availableCount = available.length;

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', py: 8 }}
        data-testid="service-area-loading"
      >
        <CircularProgress aria-label="Loading service areas" />
      </Box>
    );
  }

  if (error && available.length === 0) {
    return (
      <Alert severity="info" data-testid="service-area-error">
        {error}
      </Alert>
    );
  }

  return (
    <Box data-testid="service-area-page">
      {/* Header card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <MapOutlinedIcon color="primary" aria-hidden="true" />
            <Typography variant="h4" component="h1">
              Service Areas
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            Select the ZIP code areas you're willing to travel to and serve.
            Only active areas approved by Careva administrators are shown. Your
            selections don't take effect until you accept the confirmation
            agreement below.
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="service-area-error">
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 2 }}
          data-testid="service-area-save-success"
        >
          Your service areas have been saved.
        </Alert>
      )}

      {/* Agreement gate */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Confirmation Agreement
          </Typography>
          {agreementAccepted ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                You've accepted the service area agreement.
              </Typography>
            </Stack>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Before your service area selections can be saved, you must
                confirm the agreement below.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    inputProps={
                      {
                        'data-testid': 'service-area-agreement-checkbox',
                        'aria-label': AGREEMENT_LABEL,
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label={AGREEMENT_LABEL}
              />
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={submittingAgreement || !agreementChecked}
                  onClick={handleAgreementAccept}
                  data-testid="service-area-accept-agreement"
                >
                  {submittingAgreement ? 'Saving…' : 'Accept agreement'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Area selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available areas
            {availableCount > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({selectedCount} of {availableCount} selected)
              </Typography>
            )}
          </Typography>

          {available.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No service areas are currently active. Check back later.
            </Typography>
          ) : (
            <FormGroup>
              {available.map((area) => (
                <FormControlLabel
                  key={area.id}
                  control={
                    <Checkbox
                      checked={selected.has(area.id)}
                      onChange={() => toggleArea(area.id)}
                      inputProps={
                        {
                          'data-testid': `service-area-option-${area.zip}`,
                          'aria-label': `Select ${areaLabel(area)}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  }
                  label={areaLabel(area)}
                />
              ))}
            </FormGroup>
          )}
        </CardContent>
      </Card>

      {/* Save / back actions */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          color="primary"
          disabled={savingAreas || available.length === 0}
          onClick={handleSave}
          data-testid="service-area-save"
        >
          {savingAreas ? 'Saving…' : 'Save service areas'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/caregiver-profile-management')}
          data-testid="service-area-back"
        >
          Back to profile
        </Button>
      </Stack>
    </Box>
  );
};

export default CaregiverServiceAreasPage;
